import {useState} from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  InputBasePropsColorOverrides,
} from '@mui/material';
import {OverridableStringUnion} from '@mui/types';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

/**
 * Password input field.
 */
export const PasswordInput = (props: {
  label: string;
  handleChange: (value?: string) => void;
  handleBlur?: () => void;
  autocomplete?: 'new-password' | 'current-password';
  value?: string;
  error?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    InputBasePropsColorOverrides
  >;
}) => {
  // Hold states
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // On mouse down on the password visibility icon
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // On click on the password visibility icon
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Error
  const showError: boolean = props.error ? true : false;

  return (
    <Box className={props.className}>
      <FormControl error={showError} fullWidth variant="outlined">
        <InputLabel error={showError} color={props.color || 'neutral'} shrink={true}>
          {props.required === true ? `${props.label} *` : props.label}
        </InputLabel>
        <OutlinedInput
          notched
          autoComplete={props.autocomplete}
          label={props.required === true ? `${props.label} *` : props.label}
          disabled={props.disabled === true ? true : false}
          value={props.value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            props.handleChange(event.target.value);
          }}
          error={showError}
          type={showPassword ? 'text' : 'password'}
          color={props.color || 'neutral'}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
          onBlur={props.handleBlur}
        />
        {showError && <FormHelperText>{props.error}</FormHelperText>}
      </FormControl>
    </Box>
  );
};
